import { MenuIcons } from "./images";
import type { PageConfig } from "./modules";
import routes from "./routes";

export const reportGroups = {
  operations: {
    title: { id: "reports.operations", defaultMessage: "Operations" },
    icon: MenuIcons.PlanningIcon,
  },
  inventory: {
    title: { id: "inventory" },
    icon: MenuIcons.InventoryIcon,
  },
  finance: {
    title: { id: "finance" },
    icon: MenuIcons.FinanceIcon,
  },
};

export type ReportGroup = keyof typeof reportGroups;

export const reports: Array<
  Omit<PageConfig, "module" | "menus"> & {
    group: ReportGroup;
  }
> = [
  {
    title: { id: "activities", defaultMessage: "activities" },
    description: { id: "activityReport.description" },
    route: routes.reports.activities,
    group: "operations",
    visible: (p) => p.report?.activity,
  },
  {
    title: { id: "cropFields", defaultMessage: "cropFields" },
    description: { id: "cropFieldReport.description" },
    route: routes.reports.cropFields,
    group: "operations",
    visible: (p) => p.report?.cropField,
  },
  {
    title: { id: "laborReport.pageTitle", defaultMessage: "Labor Report" },
    description: { id: "laborReport.description" },
    route: routes.reports.labor,
    group: "operations",
    visible: (p, t) => p.report?.labor && !!t.features.employees,
  },
  {
    title: {
      id: "irrigationReport.pageTitle",
      defaultMessage: "Irrigation Report",
    },
    description: { id: "irrigationReport.description" },
    route: routes.reports.irrigation,
    group: "operations",
    visible: (p, t) => p.report?.irrigation && !!t.features.advancedActivities,
  },
  {
    title: {
      id: "fieldHealthReport.pageTitle",
      defaultMessage: "Field Health Report",
    },
    description: { id: "fieldHealthReport.description" },
    route: routes.reports.fieldHealth,
    group: "operations",
    visible: (p, t) => p.report?.nutrition && !!t.features.advancedActivities,
  },
  {
    title: {
      id: "expenseReport.pageTitle",
      defaultMessage: "Expense",
    },
    description: { id: "expenseReport.description" },
    route: routes.reports.expense,
    group: "operations",
    visible: (p) => p.report?.expense,
  },
  {
    title: { id: "generalLedger", defaultMessage: "generalLedger" },
    description: { id: "generalLedger.description" },
    route: routes.reports.generalLedger,
    group: "finance",
    visible: (p, t) => !!t.features.accounting && p.account?.write,
  },
  {
    title: { id: "ledgerReport.pageTitle" },
    description: { id: "ledgerReport.description" },
    route: routes.reports.ledger(),
    group: "finance",
    visible: (p, t) => !!t.features.accounting && p.account?.write,
  },
  {
    title: { id: "reports.harvest.pageTitle", defaultMessage: "harvestReport" },
    description: { id: "reports.harvest.description" },
    route: routes.reports.harvest,
    group: "operations",
    visible: (p, t) => p.report?.harvest && !!t.features.advancedActivities,
  },
  {
    title: {
      id: "inputCostCenterReport.pageTitle",
      defaultMessage: "Input per Cost Center",
    },
    description: { id: "inputCostCenterReport.description" },
    route: routes.reports.inputCostCenter,
    group: "inventory",
    visible: (p) => p.report?.inputCostCenter,
  },
  {
    title: {
      id: "incomeStatementReport.pageTitle",
      defaultMessage: "Income Statement",
    },
    description: { id: "incomeStatementReport.description" },
    route: routes.reports.incomeStatement,
    group: "finance",
    visible: (p, t) => !!t.features.accounting && p.report?.incomeStatement,
  },
  {
    title: { id: "inventoryMovements", defaultMessage: "inventoryMovements" },
    description: { id: "inventoryMovements.description" },
    route: routes.reports.inventoryMovements(),
    group: "inventory",
    visible: (p) => p.report?.inventoryMovement,
  },
  {
    title: { id: "inventoryEntries", defaultMessage: "Inventory Entries" },
    description: { id: "inventoryEntries.description" },
    route: routes.reports.inventoryEntries,
    group: "inventory",
    visible: (p, t) =>
      p.report?.inventoryMovement && !!t.features.inventoryStock,
  },
  {
    title: { id: "journalReport", defaultMessage: "journalReport" },
    description: { id: "journalReport.description" },
    route: routes.reports.journal,
    group: "finance",
    visible: (p, t) => !!t.features.accounting && p.account?.write,
  },
  {
    title: { id: "machines", defaultMessage: "machines" },
    description: { id: "machinesReport.description" },
    route: routes.reports.machines,
    group: "operations",
    visible: (p, t) => p.report?.machine && !!t.features.machinery,
  },
  {
    title: { id: "payrollReport.pageTitle", defaultMessage: "Pre-Payroll" },
    description: { id: "payrollReport.description" },
    route: routes.reports.prePayroll,
    group: "operations",
    visible: (p, t) => p.report?.payroll && !!t.features.employees,
  },
  {
    title: { id: "stockReport.pageTitle", defaultMessage: "Stock Report" },
    description: { id: "stockReport.description" },
    route: routes.reports.stock,
    group: "inventory",
    visible: (p, t) => p.report?.stock && !!t.features.inventoryStock,
  },
  {
    title: { id: "trialBalance", defaultMessage: "trialBalance" },
    description: { id: "trialBalance.description" },
    route: routes.reports.trialBalance,
    group: "finance",
    visible: (p, t) => !!t.features.accounting && p.account?.write,
  },
  {
    title: { id: "balanceSheet", defaultMessage: "Balance Sheet" },
    description: { id: "balanceSheet.description" },
    route: routes.reports.balanceSheet,
    group: "finance",
    visible: (p, t) =>
      !!t.features.accounting && p.account?.write && p.report?.balanceSheet,
  },
  {
    title: { id: "tokenReport.pageTitle", defaultMessage: "tokenReport" },
    description: { id: "tokenReport.description" },
    route: routes.reports.token,
    group: "operations",
    visible: (p, t) => p.report?.token && !!t.features.tokens,
  },
  {
    title: {
      id: "workOrderDailyReport.pageTitle",
      defaultMessage: "Work Orders Daily",
    },
    description: { id: "workOrderDailyReport.description" },
    route: routes.reports.workOrderDaily,
    group: "operations",
    visible: (p) => p.report?.workOrder,
  },
  {
    title: {
      id: "reports.accountsPayable.pageTitle",
    },
    description: { id: "reports.accountsPayable.description" },
    route: routes.reports.accountsPayable,
    group: "finance",
    visible: (p, t) => p.financeInvoice?.read && !!t.features.inventoryStock,
  },
  {
    title: {
      id: "reports.accountsReceivable.pageTitle",
    },
    description: { id: "reports.accountsReceivable.description" },
    route: routes.reports.accountsReceivable,
    group: "finance",
    visible: (p, t) => p.financeInvoice?.read && !!t.features.inventoryStock,
  },
];
